// Packages
import React, { useCallback, useState } from 'react';
import { gql } from '@apollo/client';

// Elements
import { Icon } from '../../../blocks/icons';
import { useToast } from '../../../hooks/useToast';
import {
	Avatar,
	Box,
	IconButton,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Typography,
} from '@mui/material';
import { Button } from '../../../blocks/Button';
import { CalendarLinks, CalendarLinksProps } from '../../CalendarLinks';
import styles from './EventRow.module.scss';

// Lib
import Globals from '../../../lib/Globals';
import { useEventRow_DismissEventMutation } from '#types';
import classNames from 'classnames';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { notEmpty } from '../../../lib/helpers/apollo';

dayjs.extend( relativeTime );

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const dismissEventMutation = gql`
	mutation EventRow_DismissEvent($where: EventWhereUniqueInput!) {
		dismissEvent(where: $where)
	}
`;

type Link = {
	type: 'primary' | 'secondary';
	text: string;
} & ( { link?: { href: string } } | { onClick?: () => void } );

export type EventRowProps = {
	className?: string;
	event: Record<string, any>;
	icon: string;
	message: string;
	links: Link[];
	hideDismissButtons: boolean; // used to determine whether to hide the dismiss buttons icon,
	hideAvatar?: true;
	hideActionButtons?: true;
	disableGutters: boolean;
	calendarLinksProps?: CalendarLinksProps;
	onCalendarMenuItemSelect?: () => void;
};

export const EventRow: React.FC<EventRowProps> = ( {
	icon,
	message,
	links,
	className,
	event,
	hideAvatar,
	hideDismissButtons,
	hideActionButtons,
	disableGutters,
	calendarLinksProps,
	onCalendarMenuItemSelect,
} ) => {
	const [ isHidden, hideEvent ] = useState( false );

	const { showError, showSuccess } = useToast();

	const [ dismissEvent ] = useEventRow_DismissEventMutation();

	const showDismissButton = hideDismissButtons
		? false
		: !Globals.nonDismissableEvents.includes( event.category );

	const onDismiss = useCallback( async () => {
		hideEvent( true );

		const result = await dismissEvent( {
			variables: { where: { id: event.id } },
		} );

		if ( result.errors ) {
			hideEvent( false );
			result.errors.map( ( error ) => showError( error ) );
		} else {
			showSuccess( 'Dismissed' );
		}
	}, [
		dismissEvent,
		event.id,
		showError,
		showSuccess
	] );

	const formattedDate = dayjs().to( event.createdAt );
	if ( isHidden ) return null;

	const eventIcon = <Icon type={ icon } className={ styles.eventIcon } />;

	const buttons = !hideActionButtons
		? links
			?.map( ( { type, text, ...action }, index ) => {
				const spreadProps =
						'link' in action && action.link
							? action.link
							: 'onClick' in action && action.onClick
								? { onClick: action.onClick }
								: undefined;
				if ( !spreadProps ) {
					return null;
				}
				return (
					<Button
						key={ `button_${ index }` }
						variant='text'
						color={ type }
						className={ styles.button }
						data-cy='event-feed-row-button'
						{ ...spreadProps }
					>
						{ text }
					</Button>
				);
			} )
			.filter( notEmpty )
		: [];

	return (
		<ListItem
			id={ event.id }
			className={ classNames( styles.container, className ) }
			dense={ !showDismissButton && hideDismissButtons }
			disableGutters={ disableGutters }
			data-cy='event-feed-row'
			data-category={ event.category }
			secondaryAction={
				showDismissButton ? (
					<IconButton onClick={ onDismiss } data-cy='event-feed-row-button'>
						<Icon type='trash' className={ styles.dismissIcon } />
					</IconButton>
				) : null
			}
		>
			{ !hideAvatar ? (
				<ListItemAvatar
					data-cy='event-feed-row-avatar'
					className={ styles.avatarContainer }
				>
					<Avatar className={ styles.avatar }>{ eventIcon }</Avatar>
				</ListItemAvatar>
			) : null }

			<ListItemText
				classes={ {
					primary: classNames(
						styles.copyAndPrimaryButtons,
						buttons && buttons.length > 1 ? styles.multipleButtons : undefined
					),
				} }
			>
				<Box
					component='span'
					className={ styles.copy }
					data-cy='event-feed-row-copy'
				>
					{ message }
					<Typography component='time' className={ styles.timestamp }>
						{ formattedDate }
					</Typography>
					{ calendarLinksProps ? (
						<Box
							key='calendar-links'
							className={ styles.calendarAction }
							data-cy='event-feed-calendar-links-slot'
						>
							<CalendarLinks
								{ ...calendarLinksProps }
								onCalendarMenuItemSelect={ onCalendarMenuItemSelect }
							/>
						</Box>
					) : null }
				</Box>
				{ buttons }
			</ListItemText>
		</ListItem>
	);
};

export default EventRow;
